import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Scope from "./Scope";
import Build from "./Build";
import VerdictBar from "./VerdictBar";
import { duration, on, type Run } from "../api";

/**
 * A run as a card, for the widths a table cannot have.
 *
 * SHARED, because the bench page and the run list both need it and both had
 * the same defect: `overflow-x-auto` around a five-column table means that at
 * 390 px the reader sees the name and the date, and the result - the column
 * somebody checks a phone FOR - sits off the right edge behind a scroll
 * nothing suggests. Two copies of the fix would be two things to forget.
 */
export default function RunCard({ run, bench, showRef = true }: { run: Run; bench?: boolean; showRef?: boolean }) {
  const { t } = useTranslation();
  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-baseline gap-2">
        <Link className="font-medium hover:underline" to={`/runs/${run.id}`}>
          {run.name}
        </Link>
        <Scope run={run} />
        {bench && <span className="ml-auto text-xs text-slate-500">{run.bench_name}</span>}
      </div>
      {run.reason && (
        <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300" data-testid="run-why">{run.reason}</p>
      )}
      {run.build_recorded && (
        <div className="mt-1.5">
          <Build build={run.build} ref_={run.build_ref} recorded showRef={showRef}
                 variant={run.variant} variantLabel={run.variant_label} />
        </div>
      )}
      <div className="mt-1.5"><VerdictBar run={run} width={140} /></div>
      <p className="mt-1.5 text-xs text-slate-500">
        {on(run.started_at)} · {duration(run.started_at, run.finished_at)} ·{" "}
        {t("run.steps", { count: run.step_count })}
      </p>
    </li>
  );
}
